/**
 * Shared types + apply logic for WORK-ITEM suggestions, used by the queue on
 * `/suggestions/work-items` and by the saved-analysis history view (revisit →
 * apply the ones skipped at the time).
 *
 * A suggestion is applied by driving the *existing* work-item / idea /
 * comment endpoints — never a bespoke "apply" endpoint — so all permission
 * checks and GitLab write-through stay in one place.
 *
 * Proposed TIME ENTRIES are a different entity with a different reviewer and
 * a different apply path; see `lib/time-suggestions.ts`.
 */

import { ApiError, api } from "@/lib/api";
import { i18n } from "@/lib/i18n";

export type Priority = "low" | "normal" | "high" | "urgent";
export type SourceType = "meeting" | "whatsapp";

export type Suggestion =
  | {
      action: "create_work_item";
      kind: "feature" | "bug" | "task" | "hotfix" | "idea";
      projectId: string;
      title: string;
      description: string;
      priority: Priority;
      /** Who should do it. Empty / absent means "let Hub decide" — the create
       *  endpoint falls back to the project's manager, since a work item is
       *  never left without an assignee. */
      assigneeId?: string;
      /** That person's name, stamped server-side from the project roster, so
       *  the card can show who it is without loading the team first. */
      assigneeName?: string;
      /** Where a new FEATURE starts. Absent = the workflow's first state;
       *  "estimation" means it has to be estimated before anything else. */
      initialState?: "new" | "estimation";
      rationale: string;
      evidence: string;
    }
  | {
      action: "add_comment";
      workItemId: string;
      comment: string;
      rationale: string;
      evidence: string;
    }
  | {
      action: "change_priority";
      workItemId: string;
      newPriority: Priority;
      rationale: string;
      evidence: string;
    }
  | {
      action: "augment_description";
      workItemId: string;
      newDescription: string;
      rationale: string;
      evidence: string;
    }
  | {
      action: "revise_description";
      workItemId: string;
      /** What the meeting changed, in Hubert's words. */
      change: string;
      /** The full description the rewrite was made against. */
      baseDescription: string;
      newDescription: string;
      rationale: string;
      evidence: string;
    }
  | {
      action: "set_fixed_price";
      workItemId: string;
      fixedPrice: number;
      rationale: string;
      evidence: string;
    }
  | {
      action: "change_estimate";
      workItemId: string;
      estimatedHours: number;
      rationale: string;
      evidence: string;
    }
  | {
      // The conversation says the ticket MOVED — the client signed off, testing
      // failed, work restarted. Unlike every other action this one isn't
      // applied through a work-item endpoint: approving it IS the write, and
      // the server performs it gitlab-first. `newStatus` is always one of the
      // target item's own workflow states.
      action: "change_status";
      workItemId: string;
      newStatus: string;
      rationale: string;
      evidence: string;
    }
  | {
      // The conversation hands an existing ticket to somebody else. Applied
      // through the normal work-item PATCH, so the GitLab write-through and
      // the change journal come along for free.
      action: "change_assignee";
      workItemId: string;
      assigneeId: string;
      assigneeName?: string;
      rationale: string;
      evidence: string;
    }
  | {
      // Proposed by the duplicate-finder scan: a cluster of work items that
      // track the same work. `canonicalId` is the one to keep; `duplicates`
      // are folded into it. `duplicateId`/`duplicateTitle` are the legacy
      // single-duplicate shape kept for older saved suggestions.
      action: "merge_duplicate";
      canonicalId: string;
      canonicalTitle: string;
      duplicates?: Array<{ id: string; title: string }>;
      duplicateId?: string;
      duplicateTitle?: string;
      rationale: string;
      evidence: string;
    };

export type SuggestionStatus = "pending" | "approved" | "rejected";

/** What the analysis remembers about each work item it was shown. `status` and
 *  `statuses` (the item's own workflow states) arrived with `change_status`, so
 *  they're absent on older runs and on duplicate-scan rows. */
export type WorkItemIndexEntry = {
  title: string;
  projectName: string;
  /** Which project it belongs to, so the card can load that project's roster
   *  for the assignee picker. Absent on older runs. */
  projectId?: string;
  status?: string | null;
  statuses?: string[];
  /** Who has the item now — what a proposed handover is measured against. */
  assigneeId?: string | null;
  assigneeName?: string | null;
};

/** A persisted suggestion row as the API returns it (history detail,
 *  suggestions queue): the proposal plus its decision state and stable id. */
export type SavedSuggestion = {
  id: string;
  payload: Suggestion;
  status: SuggestionStatus;
  appliedKind?: "wi" | "idea" | null;
  appliedId?: string | null;
  /** Why it was rejected. Hub fills this in itself when a proposal duplicates
   *  one already waiting in the queue. */
  rejectedReason?: string | null;
};

export type ApplyState = {
  status: "idle" | "applying" | "done" | "error" | "rejecting" | "rejected";
  message?: string;
  /** Where the resulting record lives, for the "open" link. */
  link?: { kind: "wi" | "idea"; id: string } | undefined;
  /** The spec changed under a rewrite; it has to be written again. */
  conflict?: "description_changed";
};

/** Getters, so the text follows the UI language at the moment it is read. */
export const ACTION_LABEL: Record<Suggestion["action"], string> = {
  get create_work_item() {
    return i18n.t("analysisQueue.card.action.create_work_item");
  },
  get add_comment() {
    return i18n.t("analysisQueue.card.action.add_comment");
  },
  get change_priority() {
    return i18n.t("analysisQueue.card.action.change_priority");
  },
  get augment_description() {
    return i18n.t("analysisQueue.card.action.augment_description");
  },
  get revise_description() {
    return i18n.t("analysisQueue.card.action.revise_description");
  },
  get set_fixed_price() {
    return i18n.t("analysisQueue.card.action.set_fixed_price");
  },
  get change_estimate() {
    return i18n.t("analysisQueue.card.action.change_estimate");
  },
  get change_status() {
    return i18n.t("analysisQueue.card.action.change_status");
  },
  get change_assignee() {
    return i18n.t("analysisQueue.card.action.change_assignee");
  },
  get merge_duplicate() {
    return i18n.t("analysisQueue.card.action.merge_duplicate");
  },
};

/** Human-friendly rendering of a workflow state, mirroring the server's
 *  `displayStateName` — the raw ids (`client_test_test`) are unreadable. */
export function displayStatus(name: string): string {
  const LABELS: Record<string, string> = {
    spec_drafting: "creating specification",
    spec_client_review: "specification approval",
    internal_test_dev: "internal testing on dev",
    client_test_dev: "client testing on dev",
    internal_test_test: "internal testing on test",
    client_test_test: "client testing on test",
    internal_test_main: "internal testing on production",
    client_test_main: "client testing on production",
  };
  return LABELS[name] ?? name.replace(/_/g, " ");
}

export const PRIORITIES: Priority[] = ["low", "normal", "high", "urgent"];

/** The duplicates a merge suggestion folds in, normalising the legacy
 *  single-duplicate shape to the cluster shape. */
export function mergeDuplicates(
  s: Extract<Suggestion, { action: "merge_duplicate" }>,
): Array<{ id: string; title: string }> {
  if (s.duplicates && s.duplicates.length > 0) return s.duplicates;
  if (s.duplicateId) return [{ id: s.duplicateId, title: s.duplicateTitle ?? s.duplicateId }];
  return [];
}

export const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
export const TEXTAREA_CLS =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

/**
 * Apply a single suggestion through the normal write endpoints, returning
 * the resulting card state. A `create_work_item` of kind `idea` posts to
 * `/api/ideas`; the other kinds post to `/api/work-items`; comment / priority
 * / spec changes patch the targeted work item.
 */
export async function applySuggestion(s: Suggestion, suggestionId?: string): Promise<ApplyState> {
  try {
    if (s.action === "create_work_item") {
      if (s.kind === "idea") {
        const created = await api.post<{ id: string }>("/api/ideas", {
          title: s.title,
          description: s.description.trim() || null,
          projectId: s.projectId || null,
        });
        return {
          status: "done",
          message: i18n.t("analysisMessages.ideaCreated"),
          link: { kind: "idea", id: created.id },
        };
      }
      if (!s.projectId) {
        return { status: "error", message: i18n.t("analysisMessages.pickProjectWi") };
      }
      const created = await api.post<{ id: string }>("/api/work-items", {
        type:
          s.kind === "hotfix"
            ? "hotfix"
            : s.kind === "bug"
              ? "bug"
              : s.kind === "task"
                ? "standalone"
                : "feature",
        projectId: s.projectId,
        title: s.title,
        description: s.description.trim() || null,
        priority: s.priority,
        // Left empty, the server resolves the project's manager — a work item
        // is never created without an assignee.
        ...(s.assigneeId ? { assigneeId: s.assigneeId } : {}),
        // Only a feature can start anywhere but the first state — a bug
        // has no estimation phase.
        ...(s.kind === "feature" && s.initialState ? { initialStateName: s.initialState } : {}),
      });
      return {
        status: "done",
        message:
          s.kind === "hotfix"
            ? i18n.t("analysisMessages.hotfixCreated")
            : s.kind === "bug"
              ? i18n.t("analysisMessages.bugCreated")
              : s.kind === "task"
                ? i18n.t("analysisMessages.taskCreated")
                : i18n.t("analysisMessages.wiCreated"),
        link: { kind: "wi", id: created.id },
      };
    }
    if (s.action === "add_comment") {
      if (!s.workItemId) {
        return { status: "error", message: i18n.t("analysisMessages.pickTargetComment") };
      }
      await api.post(`/api/work-items/${s.workItemId}/comments`, { content: s.comment });
      return {
        status: "done",
        message: i18n.t("analysisMessages.commentAdded"),
        link: { kind: "wi", id: s.workItemId },
      };
    }
    if (s.action === "change_assignee") {
      if (!s.workItemId) return { status: "error", message: i18n.t("analysisMessages.pickTarget") };
      if (!s.assigneeId)
        return { status: "error", message: i18n.t("analysisMessages.pickAssignee") };
      await api.patch(`/api/work-items/${s.workItemId}`, { assigneeId: s.assigneeId });
      return {
        status: "done",
        message: s.assigneeName
          ? i18n.t("analysisMessages.assignedTo", { name: s.assigneeName })
          : i18n.t("analysisMessages.assigneeUpdated"),
        link: { kind: "wi", id: s.workItemId },
      };
    }
    if (s.action === "change_priority") {
      await api.patch(`/api/work-items/${s.workItemId}`, { priority: s.newPriority });
      return {
        status: "done",
        message: i18n.t("analysisMessages.priorityUpdated"),
        link: { kind: "wi", id: s.workItemId },
      };
    }
    if (s.action === "set_fixed_price") {
      if (!s.workItemId) return { status: "error", message: i18n.t("analysisMessages.pickTarget") };
      await api.patch(`/api/work-items/${s.workItemId}`, {
        billingMode: "fixed",
        fixedPrice: s.fixedPrice,
      });
      return {
        status: "done",
        message: i18n.t("analysisMessages.fixedPriceSet"),
        link: { kind: "wi", id: s.workItemId },
      };
    }
    if (s.action === "change_estimate") {
      if (!s.workItemId) return { status: "error", message: i18n.t("analysisMessages.pickTarget") };
      await api.patch(`/api/work-items/${s.workItemId}`, { estimatedHours: s.estimatedHours });
      return {
        status: "done",
        message: i18n.t("analysisMessages.estimateUpdated"),
        link: { kind: "wi", id: s.workItemId },
      };
    }
    if (s.action === "change_status") {
      // A workflow move has no client-side write: the approval PATCH performs
      // it server-side, gitlab-first. `approveSuggestion` routes it there.
      return { status: "error", message: i18n.t("analysisMessages.approveStatus") };
    }
    if (s.action === "merge_duplicate") {
      // Fold every duplicate in the cluster into the canonical (each: link +
      // content move + close). Resilient to an item already merged/closed by a
      // prior run — that's a skip, not a failure.
      const dups = mergeDuplicates(s);
      if (dups.length === 0)
        return { status: "error", message: i18n.t("analysisMessages.nothingToMerge") };
      let merged = 0;
      for (const d of dups) {
        try {
          await api.post(`/api/work-items/${d.id}/merge-into`, { targetId: s.canonicalId });
          merged += 1;
        } catch (err) {
          const detail =
            err instanceof ApiError && err.detail && typeof err.detail === "object"
              ? (err.detail as { error?: string }).error
              : undefined;
          if (detail?.toLowerCase().includes("already closed")) continue; // skip, already done
          return { status: "error", message: detail ?? i18n.t("analysisMessages.mergeFailed") };
        }
      }
      return {
        status: "done",
        message:
          merged > 0
            ? i18n.t("analysisMessages.merged", { count: merged })
            : i18n.t("analysisMessages.alreadyMerged"),
        link: { kind: "wi", id: s.canonicalId },
      };
    }
    if (s.action === "revise_description") {
      try {
        await api.patch(`/api/work-items/${s.workItemId}`, {
          descriptionRevision: { base: s.baseDescription, next: s.newDescription },
          ...(suggestionId ? { suggestionId } : {}),
        });
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          return {
            status: "error",
            message: i18n.t("analysisMessages.specConflict"),
            conflict: "description_changed",
          };
        }
        throw err;
      }
      return {
        status: "done",
        message: i18n.t("analysisMessages.specChanged"),
        link: { kind: "wi", id: s.workItemId },
      };
    }
    // augment_description — append-only: the server preserves the existing
    // description and adds this new detail under a separator.
    await api.patch(`/api/work-items/${s.workItemId}`, {
      descriptionAppend: s.newDescription,
      ...(suggestionId ? { suggestionId } : {}),
    });
    return {
      status: "done",
      message: i18n.t("analysisMessages.specExtended"),
      link: { kind: "wi", id: s.workItemId },
    };
  } catch (err) {
    const message =
      err instanceof ApiError && err.status === 502
        ? i18n.t("miscLib.gitlabRejected")
        : i18n.t("miscLib.applyFailed");
    return { status: "error", message };
  }
}

/** Record on the saved run that a suggestion was approved (applied), storing
 *  the (possibly edited) payload and where it landed. Best-effort: the apply
 *  already succeeded for the user regardless. */
export async function persistApproval(
  suggestionId: string,
  payload: Suggestion,
  link?: { kind: "wi" | "idea"; id: string },
): Promise<void> {
  await api.patch(`/api/work-item-suggestions/${suggestionId}`, {
    status: "approved",
    payload,
    appliedKind: link?.kind,
    appliedId: link?.id,
  });
}

/** Record that a suggestion was rejected (dismissed without applying).
 *  `reason` is optional and free-form — why it was wrong, not just that it
 *  was. It's the sharpest signal Hubert gets on its own proposals, so it's
 *  worth writing down even though nothing reads it back yet. */
export async function persistRejection(suggestionId: string, reason?: string): Promise<void> {
  const rejectedReason = reason?.trim();
  await api.patch(`/api/work-item-suggestions/${suggestionId}`, {
    status: "rejected",
    ...(rejectedReason ? { rejectedReason } : {}),
  });
}

/**
 * Approve one suggestion: apply it, then record the decision.
 *
 * For most actions the write goes through the ordinary work-item / idea /
 * time-entry endpoints and the approval PATCH is just bookkeeping — if that
 * bookkeeping fails the user's change still landed, so it's swallowed.
 *
 * `change_status` inverts this. A workflow move has no client-side endpoint;
 * the approval PATCH performs it server-side, gitlab-first. So there the PATCH
 * *is* the apply, and its failure is the user's failure — it's reported, and
 * the suggestion stays pending.
 */
export async function approveSuggestion(
  suggestionId: string | undefined,
  s: Suggestion,
): Promise<ApplyState> {
  if (s.action === "change_status") {
    if (!suggestionId) return { status: "error", message: i18n.t("analysisMessages.notSaved") };
    try {
      await persistApproval(suggestionId, s, { kind: "wi", id: s.workItemId });
      return {
        status: "done",
        message: i18n.t("analysisMessages.movedTo", {
          status: i18n.t(`analysisQueue.card.state.${s.newStatus}`, {
            defaultValue: displayStatus(s.newStatus),
          }),
        }),
        link: { kind: "wi", id: s.workItemId },
      };
    } catch (err) {
      const detail =
        err instanceof ApiError && err.detail && typeof err.detail === "object"
          ? (err.detail as { error?: string }).error
          : undefined;
      return { status: "error", message: detail ?? i18n.t("analysisMessages.statusFailed") };
    }
  }

  const next = await applySuggestion(s, suggestionId);
  if (next.status === "done" && suggestionId) {
    try {
      await persistApproval(suggestionId, s, next.link);
    } catch {
      /* the apply succeeded; the recorded decision just didn't persist */
    }
  }
  return next;
}

/** Write a spec rewrite again against the description as it reads now. */
export async function redraftRevision(suggestionId: string): Promise<Suggestion> {
  const res = await api.post<{ payload: Suggestion }>(
    `/api/work-item-suggestions/${suggestionId}/redraft`,
    undefined,
    { timeoutMs: 180_000 },
  );
  return res.payload;
}
